import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import Autocomplete from 'components/autocomplete';
import DatePicker from 'components/date-picker';
import { useAccountState } from 'contexts/account/account-context';
import { useGet } from 'hooks/use-get';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import _ from 'underscore';
import { getMortalityProfiles } from 'views/bluviu-nutrition/features/scenarios-v2/api/mortality-profiles-get';
import { R2ScenarioParams } from 'views/bluviu-nutrition/features/scenarios-v2/types/r2-scenario-params.type';
import { UpdateScenarioOverviewInput } from 'views/bluviu-nutrition/features/scenarios-v2/types/update-scenario-overview-input.type';

const ProductionInputsSection: React.FC = () => {
  const intl = useIntl();
  const {
    control,
    formState: { submitCount },
    trigger,
  } = useFormContext<UpdateScenarioOverviewInput>();
  const watchedCageData = useWatch({ control, name: 'cageData' });
  const watchedEndDate = useWatch({ control, name: 'endDate' });
  const latestIncludedFishGroupStartDate = useMemo(
    () =>
      watchedCageData?.reduce<string | undefined>((latestStartDate, cage) => {
        if (!cage.isIncluded || !cage.startDate) return latestStartDate;
        if (!latestStartDate) return cage.startDate;

        return dayjs(cage.startDate).isAfter(dayjs(latestStartDate)) ? cage.startDate : latestStartDate;
      }, undefined),
    [watchedCageData]
  );

  const {
    account: { company },
  } = useAccountState();
  const params = useParams<R2ScenarioParams>();

  const { data: mortalityProfiles, isLoading: mortalityProfilesIsLoading } = useGet(
    ['getMortalityProfiles', company?.marketId, company?.companyId, params.globalSiteId],
    ({ queryKey }) => getMortalityProfiles(String(queryKey[1]), String(queryKey[2]), String(queryKey[3])),
    {
      enabled: !!company?.marketId && !!company?.companyId && !!params.globalSiteId,
    }
  );

  useEffect(() => {
    if (watchedEndDate || submitCount > 0) {
      trigger('endDate');
    }
  }, [latestIncludedFishGroupStartDate, submitCount, trigger, watchedEndDate]);

  return (
    <Box display={'flex'} flexDirection={'column'} gap={3}>
      <Box display={'flex'} flexDirection={'column'} gap={1}>
        <Typography fontWeight={'600'}>Production inputs</Typography>
        <Typography color={'text.secondary'}>Applies to all fish groups and can be added and adjusted in the results.</Typography>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 3,
        }}>
        <Box display="flex" flexDirection="column" gap={2}>
          <Autocomplete
            dark
            label={'Mortality profile'}
            fullWidth
            disabled={mortalityProfilesIsLoading}
            loading={mortalityProfilesIsLoading}
            disableCloseOnSelect={false}
            options={_.sortBy(mortalityProfiles ?? [], (profile) => profile.name.toLowerCase())}
            getOptionLabel={(option) => option.name}
            controller={{
              control,
              name: 'mortalityProfile',
              rules: {
                required: true,
                validate: (value) => !!value?.id || intl.formatMessage({ id: 'form.rules.required' }),
              },
            }}
            helperText={
              <Typography color="text.secondary" fontSize={14}>
                Managed in Profiles & Strategies
              </Typography>
            }
          />
        </Box>
        <DatePicker
          dark
          label={'Scenario end date'}
          fullWidth
          controller={{
            control,
            name: 'endDate',
            rules: {
              required: intl.formatMessage({ id: 'form.rules.required' }),
              validate: (value) => {
                if (!value || !latestIncludedFishGroupStartDate) return true;

                return dayjs(value).isAfter(dayjs(latestIncludedFishGroupStartDate))
                  ? true
                  : 'End date must be after the latest scenario start date for included fish groups.';
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default ProductionInputsSection;
